/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const STEPS = [
  {
    icon: "📁",
    title: "Create Your First Folder",
    desc: "VaultSphere organises your work into folders. Each folder holds multiple files — think of it like a project folder on your computer.",
    tip: "💡 Press N anywhere on the dashboard to instantly open the New Folder dialog",
    color: "#8b4e2a",
    demo: (
      <div style={{ background: "var(--surface-2)", borderRadius: 14, padding: 18, marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <div style={{ fontSize: 34 }}>📁</div>
          <div>
            <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 15 }}>Final Year Project</div>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>📎 3 files · Active · Due in 5 days</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["#fyp", "#python", "#ml"].map(t => (
            <span key={t} style={{ background: "var(--surface-2)", borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "var(--text-2)" }}>{t}</span>
          ))}
        </div>
      </div>
    ),
  },
  {
    icon: "📎",
    title: "Upload Files Inside Folders",
    desc: "Open any folder and drop files into it. Upload PDFs, code files, images, Word docs — any file type up to 25MB each.",
    tip: "💡 Upload multiple files at once — just select them all in the file picker",
    color: "#1565c0",
    demo: (
      <div style={{ background: "var(--surface-2)", borderRadius: 14, padding: 14, marginTop: 16 }}>
        {[{ n: "report.pdf", t: "PDF", c: "#e8f0fe", tc: "#3b5bdb" }, { n: "main.py", t: "Python", c: "#e6fcf5", tc: "#2f9e44" }, { n: "design.png", t: "Image", c: "#fff3e0", tc: "#e07b00" }].map(f => (
          <div key={f.n} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
            <span style={{ fontSize: 18 }}>📄</span>
            <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{f.n}</div>
            <span style={{ padding: "2px 8px", background: f.c, color: f.tc, borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{f.t}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: "🤖",
    title: "AI File Analysis",
    desc: "Click the 🤖 AI button on any file and get an instant AI analysis — summary, key points, complexity rating and suggested tags. Powered by Groq AI.",
    tip: "💡 Works on code files, PDFs, text files and Word documents",
    color: "#6b21a8",
    demo: (
      <div style={{ background: "var(--surface-2)", borderRadius: 14, padding: 16, marginTop: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>📋 AI Summary</div>
        <div style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.7, background: "var(--surface-2)", borderRadius: 10, padding: 12, marginBottom: 10 }}>
          This Python script implements a machine learning pipeline for image classification using TensorFlow. It includes data preprocessing, model training and evaluation.
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span style={{ padding: "3px 10px", background: "var(--success-tint)", color: "var(--success)", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>Moderate</span>
          <span style={{ padding: "3px 10px", background: "var(--accent-tint)", color: "var(--text-2)", borderRadius: 20, fontSize: 11 }}>#ml</span>
          <span style={{ padding: "3px 10px", background: "var(--accent-tint)", color: "var(--text-2)", borderRadius: 20, fontSize: 11 }}>#python</span>
        </div>
      </div>
    ),
  },
  {
    icon: "🤝",
    title: "Collaborate with Teammates",
    desc: "Go to the Collaborate tab to invite teammates to your folders. They can view or edit files based on the role you assign them.",
    tip: "💡 Accepted invites appear under the Shared with Me tab on your dashboard",
    color: "#2b9244",
    demo: (
      <div style={{ background: "var(--surface-2)", borderRadius: 14, padding: 14, marginTop: 16 }}>
        {[{ n: "Bhanu", r: "Editor", c: "#fff8e1", tc: "#f57f17" }, { n: "Riya", r: "Viewer", c: "#e3f2fd", tc: "#1565c0" }, { n: "Arjun", r: "Admin", c: "#fce4ec", tc: "#c62828" }].map(m => (
          <div key={m.n} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--accent)", color: "var(--on-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{m.n[0]}</div>
            <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{m.n}</div>
            <span style={{ padding: "2px 8px", background: m.c, color: m.tc, borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{m.r}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: "🏆",
    title: "Store Your Certificates",
    desc: "Got a certificate from Coursera, NPTEL or Google? Store it in the Certificates tab! Upload the PDF and keep all your achievements in one place.",
    tip: "💡 Certificates are auto-tagged and searchable by skill or provider",
    color: "#f57f17",
    demo: (
      <div style={{ background: "var(--surface-2)", borderRadius: 14, padding: 14, marginTop: 16 }}>
        {[{ e: "☁️", n: "AWS Cloud Practitioner", i: "Amazon Web Services", d: "March 2024" }, { e: "🐍", n: "Python for Data Science", i: "Coursera · IBM", d: "Jan 2024" }, { e: "🌐", n: "Full Stack Web Dev", i: "NPTEL", d: "Dec 2023" }].map(c => (
          <div key={c.n} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
            <span style={{ fontSize: 22 }}>{c.e}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{c.n}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.i} · {c.d}</div>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: "📊",
    title: "Track Your Analytics",
    desc: "The Analytics tab shows your GitHub-style activity heatmap, files uploaded per month, folder status breakdown and badges you've earned.",
    tip: "💡 Earn badges as you use VaultSphere — upload files, collaborate, and more",
    color: "#c62828",
    demo: (
      <div style={{ background: "var(--surface-2)", borderRadius: 14, padding: 14, marginTop: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginBottom: 12 }}>
          {[{ i: "📁", n: 5, l: "Folders" }, { i: "📎", n: 12, l: "Files" }, { i: "🗓️", n: 7, l: "Days" }, { i: "🏆", n: 3, l: "Badges" }].map(s => (
            <div key={s.l} style={{ background: "var(--surface)", borderRadius: 10, padding: "8px 4px", textAlign: "center" }}>
              <div style={{ fontSize: 16 }}>{s.i}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)" }}>{s.n}</div>
              <div style={{ fontSize: 9, color: "var(--text-3)" }}>{s.l}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(14,1fr)", gap: 2 }}>
          {Array(42).fill(0).map((_, i) => (
            <div key={i} style={{ aspectRatio: "1", borderRadius: 2, background: i % 7 === 0 ? "var(--accent)" : i % 3 === 0 ? "var(--accent-soft)" : "var(--surface-2)" }} />
          ))}
        </div>
      </div>
    ),
  },
];

export default function Tutorial() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState("forward");
  const [surveyNudge, setSurveyNudge] = useState(false);

  // Build translated steps
  const STEPS = [
    { icon: "📁", titleKey: "tut_step1_title", descKey: "tut_step1_desc", tipKey: "tut_step1_tip", color: "var(--accent)" },
    { icon: "📎", titleKey: "tut_step2_title", descKey: "tut_step2_desc", tipKey: "tut_step2_tip", color: "var(--info)" },
    { icon: "🤖", titleKey: "tut_step3_title", descKey: "tut_step3_desc", tipKey: "tut_step3_tip", color: "#6b21a8" },
    { icon: "🤝", titleKey: "tut_step4_title", descKey: "tut_step4_desc", tipKey: "tut_step4_tip", color: "var(--success)" },
    { icon: "🏆", titleKey: "tut_step5_title", descKey: "tut_step5_desc", tipKey: "tut_step5_tip", color: "var(--warning)" },
    { icon: "📊", titleKey: "tut_step6_title", descKey: "tut_step6_desc", tipKey: "tut_step6_tip", color: "var(--danger)" },
  ];
  const cur = STEPS[step];

  const goTo = (next, dir) => {
    if (animating) return;
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => { setStep(next); setAnimating(false); }, 280);
  };

  const finish = () => {
    localStorage.setItem("vs_tutorial_done", "1");
    const surveyDone = localStorage.getItem("vs_survey_done");
    if (!surveyDone) {
      setSurveyNudge(true);
    } else {
      localStorage.removeItem("vs_is_new_user");
      navigate("/dashboard");
    }
  };

  // ── SURVEY NUDGE SCREEN ──
  if (surveyNudge) return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "var(--font-body)" }}>
      <style>{`
        @keyframes bounceIn{0%{opacity:0;transform:scale(.4)}60%{transform:scale(1.08)}80%{transform:scale(.96)}100%{opacity:1;transform:scale(1)}}
        @keyframes fadeSlideUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        @keyframes gentlePulse{0%,100%{transform:scale(1)}50%{transform:scale(1.03)}}
        .nudge-emoji{animation:bounceIn .7s cubic-bezier(.36,.07,.19,.97) both}
        .nudge-h{animation:fadeSlideUp .5s ease .3s both}
        .nudge-p{animation:fadeSlideUp .5s ease .45s both}
        .nudge-btns{animation:fadeSlideUp .5s ease .6s both}
        .nudge-primary{animation:gentlePulse 2.5s ease infinite 1.2s}
      `}</style>
      <div style={{ textAlign: "center", maxWidth: 440 }}>
        <div className="nudge-emoji" style={{ fontSize: 80, marginBottom: 18, lineHeight: 1 }}>📝</div>
        <h2 className="nudge-h" style={{ fontFamily: "var(--font-display)", fontSize: 26, color: "var(--text)", marginBottom: 12 }}>
          {t("tut_nudge_title")}
        </h2>
        <p className="nudge-p" style={{ fontSize: 15, color: "var(--text)", lineHeight: 1.75, marginBottom: 6 }}>
          {t("tut_nudge_desc1")}
        </p>
        <p className="nudge-p" style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.6, marginBottom: 28 }}>
          {t("tut_nudge_desc2")}
        </p>
        <div className="nudge-btns" style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          <button className="nudge-primary" onClick={() => { localStorage.removeItem("vs_is_new_user"); navigate("/survey"); }}
            style={{ padding: "14px 40px", borderRadius: 14, border: "none", background: "var(--accent)", color: "var(--on-accent)", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", width: "100%", maxWidth: 300 }}>
            {t("tut_fill_survey")}
          </button>
          <button onClick={() => { localStorage.removeItem("vs_is_new_user"); navigate("/dashboard"); }}
            style={{ padding: "12px 40px", borderRadius: 14, border: "1.5px solid var(--border)", background: "transparent", color: "var(--text-2)", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", width: "100%", maxWidth: 300 }}>
            {t("tut_maybe_later")}
          </button>
        </div>
      </div>
    </div>
  );

  const slideStyle = {
    transition: "opacity .28s ease, transform .28s ease",
    opacity: animating ? 0 : 1,
    transform: animating
      ? direction === "forward" ? "translateX(30px)" : "translateX(-30px)"
      : "translateX(0)",
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "var(--font-body)" }}>
      <style>{`
        @keyframes slideInRight{from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
      `}</style>
      <div style={{ width: "100%", maxWidth: 540, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 24, overflow: "hidden", boxShadow: "var(--shadow-lg)" }}>

        {/* Coloured progress bar */}
        <div style={{ height: 4, background: "var(--surface-2)" }}>
          <div style={{ height: "100%", background: cur.color, transition: "width .4s ease, background .4s ease", width: ((step + 1) / STEPS.length * 100) + "%" }} />
        </div>

        {/* Header */}
        <div style={{ padding: "20px 26px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "1px" }}>
            {step + 1} / {STEPS.length}
          </div>
          <button onClick={finish} style={{ background: "none", border: "none", fontSize: 12, color: "var(--text-3)", cursor: "pointer", fontFamily: "inherit", padding: "4px 8px", borderRadius: 6 }}>
            {t("tut_skip")}
          </button>
        </div>

        {/* Animated content */}
        <div style={{ padding: "18px 26px 26px", ...slideStyle }}>
          {/* Icon with coloured bg */}
          <div style={{ width: 64, height: 64, borderRadius: 18, background: cur.color + "18", border: "1.5px solid " + cur.color + "33", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, marginBottom: 14 }}>
            {cur.icon}
          </div>

          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, color: "var(--text)", marginBottom: 8 }}>{t(cur.titleKey)}</h2>
          <p style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.7, marginBottom: 12 }}>{t(cur.descKey)}</p>
          <div style={{ background: "var(--accent-tint)", border: "1px solid var(--accent-soft)", borderRadius: 10, padding: "9px 14px", fontSize: 13, color: "var(--accent)" }}>
            {t(cur.tipKey)}
          </div>

          {/* Navigation */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 22 }}>
            <button onClick={() => step > 0 && goTo(step - 1, "back")} disabled={step === 0}
              style={{ padding: "9px 18px", borderRadius: 11, border: "1.5px solid var(--border-strong)", background: "transparent", color: "var(--text-3)", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: step === 0 ? "not-allowed" : "pointer", opacity: step === 0 ? .35 : 1, transition: "all .15s" }}>
              {t("tut_back")}
            </button>
            <div style={{ display: "flex", gap: 5 }}>
              {STEPS.map((_, i) => (
                <div key={i} onClick={() => i !== step && goTo(i, i > step ? "forward" : "back")}
                  style={{ width: i === step ? 22 : 7, height: 7, borderRadius: 4, background: i === step ? cur.color : i < step ? "var(--surface-3)" : "var(--surface-2)", transition: "all .25s", cursor: "pointer" }} />
              ))}
            </div>
            {step < STEPS.length - 1 ? (
              <button onClick={() => goTo(step + 1, "forward")}
                style={{ padding: "9px 22px", borderRadius: 11, border: "none", background: "var(--accent)", color: "var(--on-accent)", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                {t("tut_next")}
              </button>
            ) : (
              <button onClick={finish}
                style={{ padding: "9px 22px", borderRadius: 11, border: "none", background: cur.color, color: "var(--text)", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                {t("tut_letsgo")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}